/**
 * Focused alternative to useStages that fetches only the stages for a single entity
 * type's pipeline. Meant for callers that already know the base entity type and want
 * to avoid the fan-out across all stage types that useStages performs.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { convertSumAmounts } from '@/utils/convertCurrency';

import {
  type AssociationType,
  createSchemaClient,
  type CrmObjectTypeSchema,
} from '../core/SchemaClient';
import {
  associationTypesQueryKey,
  useAssociationTypes,
} from './useAssociationTypes';
import {
  entityTypesQueryKey,
  fetchEntityTypeSchemas,
} from './useEntityTypeSchema';
import { useExchangeRates } from './useExchangeRates';
import {
  type CurrencyAmount,
  discoverStageTypes,
  fetchStagesByType,
  type Stage,
} from './useStages';

export type { CurrencyAmount, Stage };

/**
 * How often to re-read stages while the board is "setting up" (see
 * `isSettingUp`), and for how long before giving up and showing the empty
 * state. 5s × 24 = two minutes — comfortably past the 60s resolved-permissions
 * cache TTL that hides a just-created system pipeline's stages after the
 * notes→tasks migration, without polling an abandoned tab forever.
 */
const SETTING_UP_POLL_MS = 5_000;
const SETTING_UP_MAX_POLLS = 24;

interface PipelineStagesResult {
  stages: Stage[];
  stageTypeName: string | null;
  /**
   * The board has no stages to show but that is a transient state, not a
   * configured-empty one: either the entity type is case-kind (so it always
   * has a stage type) and the schema does not list one yet, or its stage type
   * is an Ahoy-seeded system type (never empty) and the stage read came back
   * empty — both what the notes→tasks migration looks like from a browser that
   * loaded the page before it ran. Callers render "setting up", not "no stages".
   */
  settingUp: boolean;
}

function stageTypeFor(
  baseEntityType: string,
  objectTypes: CrmObjectTypeSchema[],
  associationTypes: AssociationType[]
): string | undefined {
  return discoverStageTypes(objectTypes, associationTypes).mapping[
    baseEntityType
  ];
}

function isCaseKind(
  baseEntityType: string,
  objectTypes: CrmObjectTypeSchema[]
): boolean {
  return (
    objectTypes
      .find(t => t.entity_type_name === baseEntityType)
      ?.entity_kind.includes('case') ?? false
  );
}

/** Ahoy-seeded stage types are created with their stages; empty means "not yet". */
function isSystemStageType(stageTypeName: string): boolean {
  return stageTypeName.startsWith('ahoy_');
}

export function usePipelineStages(
  orgId: number | undefined,
  baseEntityType: string | undefined
) {
  const { currentOrg } = useOrg();
  const queryClient = useQueryClient();
  const targetCurrency = currentOrg?.defaultCurrency;

  const {
    associationTypes,
    isLoading: isAssociationTypesLoading,
    error: associationTypesError,
  } = useAssociationTypes(orgId);

  const { data: ratesData } = useExchangeRates(orgId);

  const {
    data: objectTypesData,
    error: objectTypesError,
    refetch: refetchObjectTypes,
  } = useQuery({
    queryKey: entityTypesQueryKey(orgId),
    queryFn: () => fetchEntityTypeSchemas(orgId as number),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  const objectTypes = useMemo(() => objectTypesData ?? [], [objectTypesData]);

  const objectTypesSignature = useMemo(
    () =>
      objectTypes
        .map(t => `${t.entity_type_name}:${(t.entity_kind ?? []).join(',')}`)
        .sort()
        .join('|'),
    [objectTypes]
  );

  const associationTypesSignature = useMemo(
    () =>
      associationTypes
        .map(
          a =>
            `${a.association_type_name}:${a.source_entity_type_name}:${a.target_entity_type_name}:${a.cardinality}`
        )
        .sort()
        .join('|'),
    [associationTypes]
  );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'pipeline-stages',
      orgId,
      baseEntityType,
      objectTypesSignature,
      associationTypesSignature,
    ],
    queryFn: async (): Promise<PipelineStagesResult> => {
      const entityType = baseEntityType as string;
      const org = orgId as number;
      let currentObjectTypes = objectTypes;
      let stageTypeName = stageTypeFor(
        entityType,
        currentObjectTypes,
        associationTypes
      );
      if (!stageTypeName) {
        // The cached schemas (5-minute staleTime) can predate a change that
        // gave this type its stage type — the notes→tasks migration switches
        // an org's ahoy_task* types on in one transaction. Re-read both
        // schemas once and publish the fresh copies to their shared cache
        // entries so every other consumer catches up too.
        const [freshObjectTypes, freshAssociationTypes] = await Promise.all([
          fetchEntityTypeSchemas(org),
          createSchemaClient(org).getAssociationTypes(),
        ]);
        queryClient.setQueryData(entityTypesQueryKey(org), freshObjectTypes);
        queryClient.setQueryData(
          associationTypesQueryKey(org),
          freshAssociationTypes
        );
        currentObjectTypes = freshObjectTypes;
        stageTypeName = stageTypeFor(
          entityType,
          freshObjectTypes,
          freshAssociationTypes
        );
        if (!stageTypeName) {
          return {
            stages: [],
            stageTypeName: null,
            settingUp: isCaseKind(entityType, currentObjectTypes),
          };
        }
      }
      const stages = await fetchStagesByType(org, stageTypeName);
      return {
        stages,
        stageTypeName,
        settingUp: stages.length === 0 && isSystemStageType(stageTypeName),
      };
    },
    enabled:
      !!orgId &&
      !!baseEntityType &&
      objectTypes.length > 0 &&
      !isAssociationTypesLoading &&
      (!associationTypesError || associationTypes.length > 0),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 3,
    // While setting up, keep re-reading on a short cadence (bounded) so the
    // board fills in by itself once the migration commits and the caches
    // catch up — no reload required.
    refetchInterval: query =>
      query.state.data?.settingUp &&
      query.state.dataUpdateCount <= SETTING_UP_MAX_POLLS
        ? SETTING_UP_POLL_MS
        : false,
  });

  const stableStages = useMemo(() => data?.stages ?? [], [data?.stages]);

  const stageTypeNames = useMemo(
    () => new Set(data?.stageTypeName ? [data.stageTypeName] : []),
    [data?.stageTypeName]
  );

  // True when the org expects conversion (has a defaultCurrency) but rates are
  // not yet available. Callers must check this before rendering any
  // single-currency converted total.
  const ratesUnavailable = !!targetCurrency && !ratesData?.rates;

  const stagesWithConverted = useMemo(() => {
    const rates = ratesData?.rates ?? null;
    const withConverted = stableStages.map(stage => {
      if (
        !targetCurrency ||
        !rates ||
        stage.entity_type_name !== 'ahoy_deal_pipeline_stage'
      ) {
        return stage;
      }
      return {
        ...stage,
        potentialDealTotalConverted: convertSumAmounts(
          stage.potentialDealTotal,
          targetCurrency,
          rates
        ),
        weightedDealTotalConverted: convertSumAmounts(
          stage.weightedDealTotal,
          targetCurrency,
          rates
        ),
      };
    });
    return withConverted;
  }, [stableStages, targetCurrency, ratesData]);

  const stagesByEntityId = useMemo(() => {
    const m = new Map<string, Stage>();
    for (const s of stagesWithConverted) {
      m.set(s.entity_id, s);
    }
    return m;
  }, [stagesWithConverted]);

  const getStageByEntityId = useCallback(
    (entityId: string): Stage | undefined => stagesByEntityId.get(entityId),
    [stagesByEntityId]
  );

  const getStagesForObjectType = useCallback(
    (entityType: string): Stage[] => {
      if (entityType !== baseEntityType) {
        return [];
      }
      return [...stagesWithConverted].sort((a, b) => a.order - b.order);
    },
    [stagesWithConverted, baseEntityType]
  );

  const getStagesByType = useCallback(
    (stageEntityTypeName: string): Stage[] =>
      stagesWithConverted
        .filter(s => s.entity_type_name === stageEntityTypeName)
        .sort((a, b) => a.order - b.order),
    [stagesWithConverted]
  );

  const refetchAll = useCallback(async () => {
    if (!orgId || !baseEntityType) {
      return;
    }
    await refetchObjectTypes();
    await refetch();
  }, [orgId, baseEntityType, refetchObjectTypes, refetch]);

  return {
    stages: stagesWithConverted,
    ratesUnavailable,
    stageTypeNames,
    isLoading,
    /** No stages yet, but transiently so — render "setting up", not "empty". */
    isSettingUp: data?.settingUp ?? false,
    error: error ?? objectTypesError ?? associationTypesError,
    refetch: refetchAll,
    getStagesForObjectType,
    getStagesByType,
    getStageByEntityId,
    stagesByEntityId,
  };
}
